import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import "./../assets/css/modal.css";

const AccessibilityModal = ({ isOpen, toggle }) => {
  const [highContrast, setHighContrast] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [dyslexiaFont, setDyslexiaFont] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [bigCursor, setBigCursor] = useState(false);
  const [lineHeight, setLineHeight] = useState("medium");

  // Función para aplicar el tamaño de fuente a todos los elementos
  const applyFontSizeToAllElements = (size) => {
    // Aplicar al root para que afecte a todos los elementos
    document.documentElement.style.fontSize = `${size}px`;
    
    // Aplicar específicamente a diferentes tipos de texto
    const selectors = [
      'p', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      '.mitexto', '.mitexto3', '.mitextoservicios', '.mitexto-jm',
      'li', 'td', 'th', 'label', 'small', 'strong', 'em'
    ];
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.style.fontSize = `${size}px`;
      });
    });
    
    // Aplicar a elementos con clases específicas
    const classSelectors = [
      '.sobre-nosotras-title', '.form-title', '.titlemodal'
    ];
    
    classSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.style.fontSize = `${size * 1.2}px`; // Títulos un poco más grandes
      });
    });
  };

  // Cargar preferencias guardadas al iniciar
  useEffect(() => {
    const savedMode = localStorage.getItem("highContrastMode") === "true";
    const savedHighlight = localStorage.getItem("highlightLinksMode") === "true";
    const savedDyslexia = localStorage.getItem("dyslexiaFontMode") === "true";
    const savedFontSize = parseInt(localStorage.getItem("fontSize")) || 16;
    const savedBigCursor = localStorage.getItem("bigCursorMode") === "true";
    const savedLineHeight = localStorage.getItem("lineHeight") || "medium";

    if (savedMode) {
      document.body.classList.add("high-contrast-mode");
      setHighContrast(true);
    }
    
    if (savedHighlight) {
      document.body.classList.add("highlight-links-mode");
      setHighlightLinks(true);
    }

    if (savedDyslexia) {
      document.body.classList.add("dyslexia-font-mode");
      setDyslexiaFont(true);
    }

    applyFontSizeToAllElements(savedFontSize);
    setFontSize(savedFontSize);

    if (savedBigCursor) {
      console.log("Restaurando cursor grande desde localStorage");
      document.body.classList.add("big-cursor-mode");
      setBigCursor(true);
    }

    // Aplicar altura de línea guardada
    document.body.classList.remove("line-height-small", "line-height-medium", "line-height-large");
    if (savedLineHeight !== "medium") {
      document.body.classList.add(`line-height-${savedLineHeight}`);
    }
  }, []);

  // Función para activar/desactivar alto contraste
  const toggleContrast = () => {
    if (highContrast) {
      document.body.classList.remove("high-contrast-mode");
      localStorage.setItem("highContrastMode", "false");
    } else {
      document.body.classList.add("high-contrast-mode");
      localStorage.setItem("highContrastMode", "true");
    }
    setHighContrast(!highContrast);
  };

  // Función para activar/desactivar resaltado de enlaces
  const toggleHighlightLinks = () => {
    if (highlightLinks) {
      document.body.classList.remove("highlight-links-mode");
      localStorage.setItem("highlightLinksMode", "false");
    } else {
      document.body.classList.add("highlight-links-mode");
      localStorage.setItem("highlightLinksMode", "true");
    }
    setHighlightLinks(!highlightLinks);
  };

  // Función para activar/desactivar fuente para dislexia
  const toggleDyslexiaFont = () => {
    if (dyslexiaFont) {
      document.body.classList.remove("dyslexia-font-mode");
      localStorage.setItem("dyslexiaFontMode", "false");
    } else {
      document.body.classList.add("dyslexia-font-mode");
      localStorage.setItem("dyslexiaFontMode", "true");
    }
    setDyslexiaFont(!dyslexiaFont);
  };

  // Función cíclica para cambiar el tamaño de fuente
  const toggleFontSize = () => {
    let newSize;
    if (fontSize === 16) {
      newSize = 18; // Primer clic: aumentar
    } else if (fontSize === 18) {
      newSize = 20; // Segundo clic: aumentar más
    } else if (fontSize === 20) {
      newSize = 22; // Tercer clic: aumentar aún más
    } else {
      newSize = 16; // Cuarto clic: volver al normal
    }
    
    applyFontSizeToAllElements(newSize);
    localStorage.setItem("fontSize", newSize.toString());
    setFontSize(newSize);
  };

  // Función para activar/desactivar cursor grande
  const toggleBigCursor = () => {
    console.log("toggleBigCursor llamado, bigCursor:", bigCursor);
    
    if (bigCursor) {
      // Restaurar cursor normal
      console.log("Desactivando cursor grande");
      document.body.classList.remove("big-cursor-mode");
      localStorage.setItem("bigCursorMode", "false");
    } else {
      // Activar cursor grande usando CSS
      console.log("Activando cursor grande");
      document.body.classList.add("big-cursor-mode");
      localStorage.setItem("bigCursorMode", "true");
    }
    setBigCursor(!bigCursor);
  };

  // Función cíclica para cambiar altura de línea
  const toggleLineHeight = () => {
    let newSize;
    if (lineHeight === "medium") {
      newSize = "small"; // Primer clic: pequeña
    } else if (lineHeight === "small") {
      newSize = "large"; // Segundo clic: grande
    } else if (lineHeight === "large") {
      newSize = "medium"; // Tercer clic: volver al normal
    } else {
      newSize = "medium"; // Por defecto: normal
    }
    
    document.body.classList.remove("line-height-small", "line-height-medium", "line-height-large");
    
    if (newSize !== "medium") {
      document.body.classList.add(`line-height-${newSize}`);
    }
    
    setLineHeight(newSize);
    localStorage.setItem("lineHeight", newSize);
  };

  // Estilo visual para botones activos
  const activeButtonStyle = {
    border: "3px solid #ff8800",
    borderRadius: "15px",
    backgroundColor: "#222",
  };

  return (
    <Modal 
     isOpen={isOpen} 
    toggle={toggle} 
    className="modal-right"
    backdrop="static" // Evita que se cierre haciendo clic fuera
    keyboard={false} // Evita que se cierre con ESC
    scrollable={true} // Permite scroll si el contenido es largo
    style={{ 
        margin: 0, 
        maxWidth: '500px', 
        width: '100%' 
    }}>
      <ModalHeader toggle={toggle} className="menu-de-accesibilidad">
        <p className="titlemodal">MENÚ DE ACCESIBILIDAD</p>
      </ModalHeader>
      <ModalBody>
      
        <div className="button-grid">
          {/* --- Botón ALTO CONTRASTE --- */}
          <button
            className="accessibility-button"
            onClick={toggleContrast}
            style={highContrast ? activeButtonStyle : {}}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <img
                src={require("../assets/simbolos/jpg/contraste.jpg")}
                alt="Alto Contraste"
                style={{ maxWidth: "40px", maxHeight: "40px", height: "auto" }}
              />
              <h3
                style={{
                  color: highContrast ? "#ff8800" : "black",
                  textAlign: "center",
                  marginTop: "8px",
                  fontWeight: 600,
                  fontSize: 18,
                }}
              >
                {highContrast ? "Contraste Normal" : "Alto Contraste"}
              </h3>
            </div>
          </button>

          {/* --- Botón RESALTAR ENLACES --- */}
          <button 
            className="accessibility-button"
            onClick={toggleHighlightLinks}
            style={highlightLinks ? activeButtonStyle : {}}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <img
                src={require("../assets/simbolos/jpg/resaltar.jpg")}
                alt="Resaltar enlaces"
                style={{ maxWidth: "40px", maxHeight: "40px", height: "auto" }}
              />
              <h3
                style={{
                  color: highlightLinks ? "#ff8800" : "black",
                  textAlign: "center",
                  marginTop: "8px",
                  fontWeight: 600,
                  fontSize: 18,
                }}
              >
                {highlightLinks ? "Ocultar resaltado" : "Resaltar enlaces"}
              </h3>
            </div>
          </button>

          {/* --- Botón TEXTO PARA DISLEXIA --- */}
          <button
            className="accessibility-button"
            onClick={toggleDyslexiaFont}
            style={dyslexiaFont ? activeButtonStyle : {}}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <img
                src={require("../assets/simbolos/jpg/dislexia.jpg")}
                alt="Fuente para dislexia"
                style={{ maxWidth: "40px", maxHeight: "40px", height: "auto" }}
              />
              <h3
                style={{
                  color: dyslexiaFont ? "#ff8800" : "black",
                  textAlign: "center",
                  marginTop: "8px",
                  fontWeight: 600,
                  fontSize: 18,
                }}
              >
                {dyslexiaFont ? "Fuente normal" : "Fuente dislexia"}
              </h3>
            </div>
          </button>

          {/* --- Botón CURSOR AUMENTADO --- */}
          <button 
            className="accessibility-button"
            onClick={toggleBigCursor}
            style={bigCursor ? activeButtonStyle : {}}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <img
                src={require("../assets/simbolos/jpg/cursor.jpg")}
                alt="Cursor aumentado"
                style={{ maxWidth: "40px", maxHeight: "40px", height: "auto" }}
              />
              <h3
                style={{
                  color: bigCursor ? "#ff8800" : "black",
                  textAlign: "center",
                  marginTop: "8px",
                  fontWeight: 600,
                  fontSize: 18,
                }}
              >
                {bigCursor ? "Cursor normal" : "Cursor aumentado"}
              </h3>
            </div>
          </button>

          {/* --- Botón TAMAÑO DE TEXTO --- */}
          <button 
            className="accessibility-button"
            onClick={toggleFontSize}
            style={{ padding: '10px' }}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <img
                src={require("../assets/simbolos/jpg/agrandar_texto.jpg")}
                alt="Tamaño de texto"
                style={{ maxWidth: "40px", maxHeight: "40px", height: "auto" }}
              />
              <h3
                style={{
                  color: fontSize !== 16 ? "#ff8800" : "black",
                  textAlign: "center",
                  marginTop: "8px",
                  fontWeight: 600,
                  fontSize: 18,
                }}
              >
                Tamaño texto
              </h3>
              <div style={{ marginTop: '5px', fontSize: '14px' }}>
                Actual: {fontSize}px
              </div>
            </div>
          </button>

          {/* --- Botón ALTURA DE LÍNEA --- */}
          <button 
            className="accessibility-button"
            onClick={toggleLineHeight}
            style={{ padding: '10px' }}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <img
                src={require("../assets/simbolos/jpg/altura.jpg")}
                alt="Altura de línea"
                style={{ maxWidth: "40px", maxHeight: "40px", height: "auto" }}
              />
              <h3
                style={{
                  color: lineHeight !== "medium" ? "#ff8800" : "black",
                  textAlign: "center",
                  marginTop: "8px",
                  fontWeight: 600,
                  fontSize: 18,
                }}
              >
                Altura de línea
              </h3>
              <div style={{ marginTop: '5px', fontSize: '14px', color: lineHeight !== "medium" ? "#ff8800" : "#333" }}>
                Actual: {lineHeight === "small" ? "Pequeña" : lineHeight === "large" ? "Grande" : "Normal"}
              </div>
            </div>
          </button>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-dark"
          onClick={toggle}
          style={highContrast ? { 
            backgroundColor: "black", 
            color: "white", 
            border: "2px solid white" 
          } : {}}
        >
          Cerrar
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default AccessibilityModal;